import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../../components/AdminLayout";
import Loader from "../../components/Loading/Loader";
import { listClubInquiries, patchClubInquiryStatus } from "../../api/clubInquiryApi";
import "./Dashboard.css";

const STATUSES = ["new", "in_progress", "done"];

export function ClubInquiries() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listClubInquiries();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || t("inquiriesLoadFailed", "Could not load inquiries"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatus = async (inquiryId, status) => {
    setSavingId(inquiryId);
    try {
      const updated = await patchClubInquiryStatus(inquiryId, status);
      setRows((prev) =>
        prev.map((row) => (row.inquiry_id === inquiryId ? { ...row, ...updated } : row))
      );
    } catch (err) {
      setError(err?.message || t("inquiriesUpdateFailed", "Could not update inquiry"));
    } finally {
      setSavingId("");
    }
  };

  return (
    <AdminLayout>
      <div className="title">
        <h1>{t("clubInquiriesTitle", "Club inquiries")}</h1>
      </div>
      {error ? <p className="text-danger">{error}</p> : null}
      {loading ? (
        <Loader />
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>{t("club")}</th>
                <th>{t("country")}</th>
                <th>{t("userrole")}</th>
                <th>{t("email")}</th>
                <th>{t("note", "Note")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>{t("inquiriesEmpty", "No inquiries yet.")}</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.inquiry_id}>
                    <td>{row.club_name}</td>
                    <td>{row.country}</td>
                    <td>{row.role}</td>
                    <td>{row.email}</td>
                    <td>{row.note || "—"}</td>
                    <td>
                      <select
                        value={row.status || "new"}
                        disabled={savingId === row.inquiry_id}
                        onChange={(e) => onStatus(row.inquiry_id, e.target.value)}
                        aria-label={t("status")}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s === "in_progress"
                              ? t("inquiryInProgress", "In progress")
                              : s === "done"
                                ? t("inquiryDone", "Done")
                                : t("inquiryNew", "New")}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default ClubInquiries;
